import { useEffect, useState, useCallback } from "react"; 
import API from "../api/api";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import "./CartPage.css";

export default function CartPage() {
  const navigate = useNavigate();
  const { cartCount, fetchCartCount, updateCartCountInstant } = useCart();

  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [popup, setPopup] = useState("");

  const token = localStorage.getItem("access");

  const showPopup = (msg, type = "success") => {
    setPopup({ message: msg, type });
    setTimeout(() => setPopup(""), 2000);
  };

  // Fetch cart
  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get("/cart/my-cart/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCart(res.data);
      fetchCartCount();
    } catch {
      setCart(null);
    } finally {
      setLoading(false);
    }
  }, [token, fetchCartCount]);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchCart();
  }, [fetchCart, token, navigate]);

  // Remove item
  const removeItem = async (itemId, qty) => {
    try {
      setUpdating(true);
      await API.post(
        "/cart/item/remove/",
        { item_id: itemId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart((prevCart) => {
        const updatedItems = prevCart.items.filter((item) => item.id !== itemId);
        return { ...prevCart, items: updatedItems };
      });
      updateCartCountInstant(-qty);
      showPopup("Product removed");
    } catch {
      showPopup("Failed to remove", "error");
    } finally {
      setUpdating(false);
    }
  };

  // Update qty
  const updateQty = async (itemId, newQty, oldQty, maxStock) => {
    if (newQty < 1 || newQty > maxStock) return;
    try {
      setUpdating(true);
      await API.post(
        "/cart/item/update/",
        {
          item_id: itemId,
          quantity: parseInt(newQty, 10),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart((prevCart) => {
        const updatedItems = prevCart.items.map((item) =>
          item.id === itemId ? { ...item, quantity: newQty } : item
        );
        return { ...prevCart, items: updatedItems };
      });
      updateCartCountInstant(newQty - oldQty);
      showPopup("Quantity updated");
    } catch {
      showPopup("Update failed", "error");
    } finally {
      setUpdating(false);
    }
  };

  if (loading)
    return <div className="cart-empty">Loading cart...</div>;

  if (!cart || cart.items.length === 0)
    return (
      <div className="cart-empty">
        <button className="cart-back-btn" onClick={() => navigate("/")}>
          ← Continue Shopping
        </button>
        <h2>Your cart is empty</h2>
      </div>
    );

  const total = cart.items.reduce(
    (sum, item) => sum + item.product.price * item.quantity,
    0
  );

  return (
    <div className="cart-page">
      {/* POPUP */}
      {popup && <div className={`cart-popup ${popup.type}`}>{popup.message}</div>}

      {/* HEADER */}
      <div className="cart-top-bar">
        <button className="cart-back-btn" onClick={() => navigate("/")}>
          ← Shop
        </button>
        <h2>
          Shopping Cart <span className="cart-count">({cartCount})</span>
        </h2>
      </div>

      {/* ITEMS */}
      <div className="cart-list">
        {cart.items.map((item) => (
          <div key={item.id} className="cart-card">
            <div className="cart-img-wrap">
              <img
                src={
                  item.product.product_images?.length > 0
                    ? item.product.product_images[0].image.startsWith("http")
                      ? item.product.product_images[0].image
                      : `${process.env.REACT_APP_API_BASE}${item.product.product_images[0].image}`
                    : "/placeholder.jpg"
                }
                alt={item.product.name}
              />
            </div>
            <div className="cart-info">
              <h3>{item.product.name}</h3>
              <p className="cart-meta">
                Size: <b>{item.size || "-"}</b> &nbsp;|&nbsp; Color: <b>{item.color || "-"}</b>
              </p>
              {/* QTY CONTROL */}
              <div className="qty-control">
                <button
                  disabled={updating}
                  aria-label="decrease"
                  onClick={() => updateQty(item.id, item.quantity - 1, item.quantity, item.product.quantity)}
                >
                  −
                </button>
                <span>{item.quantity}</span>
                <button
                  disabled={updating}
                  aria-label="increase"
                  onClick={() => updateQty(item.id, item.quantity + 1, item.quantity, item.product.quantity)}
                >
                  +
                </button>
              </div>
              {/* PRICE */}
              <div className="cart-price">ETB <b>{item.product.price * item.quantity}</b></div>
              {/* REMOVE */}
              <button
                className="remove-btn"
                disabled={updating}
                onClick={() => removeItem(item.id, item.quantity)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* TOTAL and CHECKOUT */}
      <div className="cart-bottom-box">
        <div className="cart-total">
          Total: <span>ETB {total}</span>
        </div>
        <button className="checkout-btn" onClick={() => navigate("/checkout")}>
          Checkout
        </button>
      </div>
    </div>
  );
}